import { IEvent } from '@trii/types/dist/Calendar';
import { ICalendar } from '@trii/types/dist/Calendar/Calendar';
import { ITask } from '@trii/types/dist/Calendar';
import { UserTrii } from '@trii/types/dist/Users';
import { requestJson } from './http';
import { getApiUrls, joinUrl } from './urls';
import type {
  AddActivityRequestData,
  UpdatedActivityRequestData,
  AddTaskRequestData,
  AddCalendarRequestData,
} from '../types';

/**
 * API Service para operaciones de Calendar, Events y Tasks
 * 
 * ENDPOINTS UTILIZADOS:
 * 
 * ═══════════════════════════════════════════════════════════
 * EVENTOS (Events)
 * ═══════════════════════════════════════════════════════════
 * GET    /api/v1/calendar/{spaceId}/Events?start={start}&end={end}
 *        - Lista eventos en un rango de fechas
 * 
 * GET    /api/v1/calendar/{spaceId}/events/{eventId}
 *        - Obtiene un evento específico por ID
 * 
 * POST   /api/v1/calendar/{spaceId}/events
 *        - Crea un nuevo evento
 * 
 * PUT    /api/v1/calendar/{spaceId}/events/{eventId}
 *        - Actualiza un evento existente
 * 
 * PUT    /api/v1/calendar/{spaceId}/events/occurrence/{eventId}?occurrence={date}
 *        - Actualiza una ocurrencia específica de un evento recurrente
 * 
 * DELETE /api/v1/calendar/{spaceId}/events/{eventId}
 *        - Elimina un evento
 * 
 * DELETE /api/v1/calendar/{spaceId}/events/{eventId}?occurrence={date}
 *        - Elimina una ocurrencia específica de un evento recurrente
 * 
 * ═══════════════════════════════════════════════════════════
 * TAREAS (Tasks)
 * ═══════════════════════════════════════════════════════════
 * GET    /api/v1/calendar/{spaceId}/Task/{calendarId}
 *        - Lista todas las tareas de un calendario
 * 
 * GET    /api/v1/calendar/{spaceId}/Task/detail/{taskId}
 *        - Obtiene una tarea específica por ID
 * 
 * POST   /api/v1/calendar/{spaceId}/Task
 *        - Crea una nueva tarea
 * 
 * PUT    /api/v1/calendar/{spaceId}/Task/{taskId}
 *        - Actualiza una tarea existente
 * 
 * DELETE /api/v1/calendar/{spaceId}/Task/{taskId}
 *        - Elimina una tarea
 * 
 * ═══════════════════════════════════════════════════════════
 * CALENDARIOS (Calendars)
 * ═══════════════════════════════════════════════════════════
 * GET    /api/v1/calendar/{spaceId}/Calendar
 *        - Lista todos los calendarios del espacio
 * 
 * POST   /api/v1/calendar/{spaceId}/Calendar
 *        - Crea un nuevo calendario
 * 
 * PUT    /api/v1/calendar/{spaceId}/Calendar/{calendarId}
 *        - Actualiza un calendario existente
 * 
 * ═══════════════════════════════════════════════════════════
 * USUARIO (User)
 * ═══════════════════════════════════════════════════════════
 * GET    /api/v1/settings/{spaceId}/user
 *        - Obtiene información del usuario actual
 */

type ApiContext = { baseUrl: string; spaceId: string };

function calendarBase(ctx: ApiContext) {
  return getApiUrls(ctx.baseUrl, ctx.spaceId).CALENDAR;
}

// ─── User ────────────────────────────────────────────────

export async function fetchUserTrii(ctx: ApiContext): Promise<UserTrii> {
  const urls = getApiUrls(ctx.baseUrl, ctx.spaceId);
  return requestJson<UserTrii>({ url: joinUrl(urls.SETTINGS, '/user') });
}

// ─── Events ──────────────────────────────────────────────

export async function fetchActivities(
  ctx: ApiContext,
  start: string,
  end: string,
): Promise<IEvent[]> {
  const base = calendarBase(ctx);
  const url = `${base}/Events?start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  return requestJson<IEvent[]>({ url });
}

export async function fetchEventById(ctx: ApiContext, eventId: string): Promise<IEvent> {
  const base = calendarBase(ctx);
  return requestJson<IEvent>({
    url: joinUrl(base, `/events/${encodeURIComponent(eventId)}`),
  });
}

export async function addActivity(ctx: ApiContext, data: AddActivityRequestData): Promise<IEvent> {
  const base = calendarBase(ctx);
  return requestJson<IEvent>({
    url: joinUrl(base, '/events'),
    method: 'POST',
    body: data,
  });
}

export async function updateActivity(
  ctx: ApiContext,
  data: UpdatedActivityRequestData,
): Promise<IEvent> {
  const base = calendarBase(ctx);
  const { activityId, updatedActivity, originalOccurrenceDate } = data;

  if (originalOccurrenceDate) {
    const dateToSend =
      originalOccurrenceDate instanceof Date
        ? originalOccurrenceDate.toISOString()
        : originalOccurrenceDate;

    const url = `${base}/events/occurrence/${activityId}?occurrence=${dateToSend}`;
    return requestJson<IEvent>({ url, method: 'PUT', body: updatedActivity });
  }

  return requestJson<IEvent>({
    url: joinUrl(base, `/events/${activityId}`),
    method: 'PUT',
    body: updatedActivity,
  });
}

export async function deleteActivity(
  ctx: ApiContext,
  activityId: string,
  originalOccurrenceDate?: Date | string,
): Promise<void> {
  const base = calendarBase(ctx);

  if (originalOccurrenceDate) {
    const dateToSend =
      originalOccurrenceDate instanceof Date
        ? originalOccurrenceDate.toISOString()
        : originalOccurrenceDate;

    const url = `${base}/events/${activityId}?occurrence=${dateToSend}`;
    await requestJson<null>({ url, method: 'DELETE' });
  } else {
    await requestJson<null>({
      url: joinUrl(base, `/events/${activityId}`),
      method: 'DELETE',
    });
  }
}

export async function endActivity(ctx: ApiContext, activityId: string): Promise<IEvent> {
  const base = calendarBase(ctx);
  return requestJson<IEvent>({
    url: joinUrl(base, `/events/${activityId}`),
    method: 'PUT',
    body: { finalized: true },
  });
}

// ─── Tasks ───────────────────────────────────────────────

export async function fetchTasks(ctx: ApiContext, calendarId: string): Promise<ITask[]> {
  const base = calendarBase(ctx);
  return requestJson<ITask[]>({ url: joinUrl(base, `/Task/${calendarId}`) });
}

export async function fetchTaskById(ctx: ApiContext, taskId: string): Promise<ITask> {
  const base = calendarBase(ctx);
  return requestJson<ITask>({
    url: joinUrl(base, `/Task/detail/${encodeURIComponent(taskId)}`),
  });
}

export async function addTask(ctx: ApiContext, data: AddTaskRequestData): Promise<ITask> {
  const base = calendarBase(ctx);
  return requestJson<ITask>({
    url: joinUrl(base, '/Task'),
    method: 'POST',
    body: data,
  });
}

export async function updateTaskApi(
  ctx: ApiContext,
  taskId: string,
  updatedTask: Record<string, unknown>,
): Promise<ITask> {
  const base = calendarBase(ctx);
  return requestJson<ITask>({
    url: joinUrl(base, `/Task/${taskId}`),
    method: 'PUT',
    body: updatedTask,
  });
}

export async function deleteTaskApi(ctx: ApiContext, taskId: string): Promise<void> {
  const base = calendarBase(ctx);
  await requestJson<null>({
    url: joinUrl(base, `/Task/${taskId}`),
    method: 'DELETE',
  });
}

export async function toggleTaskCompletedApi(
  ctx: ApiContext,
  taskId: string,
  isCompleted: boolean,
): Promise<ITask> {
  const base = calendarBase(ctx);
  return requestJson<ITask>({
    url: joinUrl(base, `/Task/${taskId}`),
    method: 'PUT',
    body: { isCompleted },
  });
}

// ─── Calendars ───────────────────────────────────────────

export async function fetchCalendars(ctx: ApiContext): Promise<ICalendar[]> {
  const base = calendarBase(ctx);
  return requestJson<ICalendar[]>({ url: joinUrl(base, '/Calendar') });
}

export async function addCalendarApi(
  ctx: ApiContext,
  data: AddCalendarRequestData,
): Promise<ICalendar> {
  const base = calendarBase(ctx);
  return requestJson<ICalendar>({
    url: joinUrl(base, '/Calendar'),
    method: 'POST',
    body: data,
  });
}

export async function updateCalendarApi(
  ctx: ApiContext,
  calendarId: string,
  data: AddCalendarRequestData,
): Promise<ICalendar> {
  const base = calendarBase(ctx);
  return requestJson<ICalendar>({
    url: joinUrl(base, `/Calendar/${calendarId}`),
    method: 'PUT',
    body: data,
  });
}
